import React, { useState, useEffect, useRef, useContext } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'
import { InputText } from 'primereact/inputtext'

import { Button } from 'primereact/button'

import server from '../../server'
import { formatCurrency, formatPercentage } from '../shared/utils'
import MainContentLayout from '../Layout/MainContentLayout'
import { InputFieldTemplate } from '../shared/FilterTemplates'
import { AuthenticationContext } from '../../Auth/authentication.context'
import useTableRowsPerPage from '../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../shared/tablePagination'
import OrderDetailsHeader from './OrderDetailsHeader'
import BackButton from '../shared/backButton'
import AS2TroubleshootingButton, {
  SUPERADMIN_EMAIL,
} from './AS2TroubleshootingButton'
import Modals from '../shared/Modals'
import { Message } from 'primereact/message'
import { InputTextarea } from 'primereact/inputtextarea'

const OrderDetails = () => {
  const { orderId } = useParams()
  const order = useLocation()

  const { isAdmin, user } = useContext(AuthenticationContext)
  // The super-admin (webmaster) is identified by email, not the 'admin' role, so
  // include it explicitly wherever admin-only order actions are gated.
  const isSuperadmin =
    String(user?.email || '')
      .trim()
      .toLowerCase() === SUPERADMIN_EMAIL
  const canManageOrder = isAdmin || isSuperadmin
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()

  const dt = useRef(null)
  const [orderDetails, setOrderDetails] = useState(null)
  const [filters, setFilters] = useState(null)
  const [state, setState] = useState(() => getInitialPaginationState(user))
  const [tableLoading, setTableLoading] = useState(false)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [globalFilterValue, setGlobalFilterValue] = useState('')

  // Header row (owned here so the badge/annotation update after an admin cancel).
  const [headerRow, setHeaderRow] = useState(order.state)

  // Admin force-cancel (button lives in the bottom toolbar beside Export CSV).
  const [cancelVisible, setCancelVisible] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelError, setCancelError] = useState('')

  // Admin reverse-cancellation (only shown while the order is Cancelled).
  const [reverseVisible, setReverseVisible] = useState(false)
  const [reverseLoading, setReverseLoading] = useState(false)
  const [reverseError, setReverseError] = useState('')

  const handleCancelOrder = async () => {
    setCancelLoading(true)
    setCancelError('')
    try {
      const response = await server.patch(
        `/invoicesHeaders/${headerRow.id}/cancel`,
        { reason: cancelReason || undefined },
        {
        }
      )
      const cancelledFromStatus =
        response.data?.cancelledFromStatus || headerRow.status
      setHeaderRow((prev) => ({
        ...prev,
        status: 'Cancelled',
        cancelledFromStatus,
        cancelledAt: new Date().toISOString(),
        cancellationReason: cancelReason || null,
      }))
      setCancelLoading(false)
      setCancelVisible(false)
      setCancelReason('')
    } catch (error) {
      setCancelError(
        error?.response?.data?.message ||
          'Failed to cancel the order. Please try again.'
      )
      setCancelLoading(false)
    }
  }

  const handleReverseOrder = async () => {
    setReverseLoading(true)
    setReverseError('')
    try {
      await server.patch(
        `/invoicesHeaders/${headerRow.id}/reverse-cancel`,
        {},
        {
        }
      )
      setHeaderRow((prev) => ({
        ...prev,
        status: 'Pending',
        cancelledFromStatus: null,
        cancelledAt: null,
        cancellationReason: null,
      }))
      setReverseLoading(false)
      setReverseVisible(false)
      // Reload line items so the restored (original) quantities are shown.
      getData()
    } catch (error) {
      setReverseError(
        error?.response?.data?.message ||
          'Failed to reverse the order. Please try again.'
      )
      setReverseLoading(false)
    }
  }

  // Buttons rendered in MainContentLayout's bottom toolbar (left of Export CSV).
  const bottomToolbarLeft = (
    <div className='d-flex align-items-center gap-2'>
      <AS2TroubleshootingButton
        orderId={headerRow?.id}
        supplierOrderNo={headerRow?.supplierOrderNo}
      />
      {isAdmin && headerRow?.status !== 'Cancelled' && (
        <Button
          label='Cancel Order'
          icon='pi pi-times-circle'
          severity='danger'
          outlined
          onClick={() => {
            setCancelError('')
            setCancelReason('')
            setCancelVisible(true)
          }}
        />
      )}
      {canManageOrder && headerRow?.status === 'Cancelled' && (
        <Button
          label='Reverse Cancellation'
          icon='pi pi-replay'
          severity='success'
          outlined
          onClick={() => {
            setReverseError('')
            setReverseVisible(true)
          }}
        />
      )}
    </div>
  )

  const getData = (customFilters) => {
    setTableLoading(true)

    // Use current filters from state if not provided
    const activeFilters = customFilters || state.filters
    const filterParams = buildFilterParams(activeFilters)

    server
      .get(`/invoicesDetails/?headerId=${orderId}&limit=1000`, {
        params: filterParams,
      })
      .then((response) => {
        const orderDetailsData = response.data.details
        setOrderDetails(orderDetailsData)
        setTableLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
      })
  }

  useEffect(() => {
    setState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  useEffect(() => {
    initState()
    return getData()
  }, [])

  useEffect(() => {
    checkClearBtn()
  }, [state])

  const checkClearBtn = () => {
    if (state.sortField || state.sortOrder) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in state?.filters) {
      if (key === 'global') {
        continue
      } else if (
        state?.filters[key].constraints[0].value ||
        state?.filters[key]?.constraints[1]?.value
      ) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  const clearFilter = () => {
    initState()
  }

  const onGlobalFilterChange = (e) => {
    const value = e.target.value
    let _filters = { ...state.filters }

    _filters['global'].value = value

    setState((prevState) => {
      return {
        ...prevState,
        filters: _filters,
      }
    })
    setGlobalFilterValue(value)
  }

  const initState = () => {
    setState((prevState) => ({
      ...prevState,
      first: 0,
      page: 0,
      rows: defaultRows,
      sortField: '',
      sortOrder: '',
      filters: {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        retailUpcCode: {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
        itemCode: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: FilterMatchMode.STARTS_WITH },
          ],
        },
        'manufacturer.name': {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
        'molecule.name': {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
        shippedQuantity: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        requestedQuantity: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        unitPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        extendedPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        refundedAmount: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        paPercentage: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        'item.itemDescription': {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
      },
    }))
    setGlobalFilterValue('')
  }

  // TOOLBAR
  const topLeftToolbarTemplate = () => (
    <Button
      type='button'
      icon='pi pi-filter-slash'
      label='Clear'
      outlined
      onClick={clearFilter}
      disabled={clearBtnDisabled}
    />
  )

  const topRightToolbarTemplate = () => (
    <InputText
      value={globalFilterValue}
      onChange={onGlobalFilterChange}
      placeholder='Keyword Search'
    />
  )

  // BODY TEMPLATES
  const unitPriceBodyTemplate = (order) => formatCurrency(order.unitPrice)

  const extendedPriceBodyTemplate = (order) =>
    formatCurrency(order.extendedPrice)

  const refundedAmountBodyTemplate = (order) =>
    formatCurrency(order.refundedAmount)

  const paPercentageBodyTemplate = (order) =>
    formatPercentage(order.paPercentage)

  // FILTER TEMPLATES
  const upcCodeFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      placehloder='UPC'
      icon='pi pi-hashtag'
      type='text'
    />
  )
  const itemDescriptionFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      placehloder='Item Description'
      icon='pi pi-search'
      type='text'
    />
  )

  const manufacturerFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      placehloder='Manufacturer'
      icon='mdi mdi-cog'
    />
  )
  const moleculeFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      placehloder='Molecule'
      icon='mdi mdi-star'
    />
  )
  const shippedQuantityFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='pi pi-hashtag'
      type='number'
    />
  )
  const requestedQuantityFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='pi pi-hashtag'
      type='number'
    />
  )
  const unitPriceFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-currency-usd'
      type='number'
    />
  )
  const extendedPriceFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-currency-usd'
      type='number'
    />
  )
  const refundedAmountFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-currency-usd'
      type='number'
    />
  )
  const paPercentageFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-percent-outline'
      type='number'
    />
  )

  const buildFilterParams = (filters) => {
    const params = {}
    if (!filters) return params
    Object.keys(filters).forEach((key) => {
      const filter = filters[key]
      if (filter && filter.constraints) {
        if (filter.constraints.length === 1) {
          const constraint = filter.constraints[0]
          if (constraint.value !== null && constraint.value !== '') {
            params[key] = constraint.value
          }
        } else {
          filter.constraints.forEach((constraint, idx) => {
            if (constraint.value !== null && constraint.value !== '') {
              params[`${key}_${idx}`] = constraint.value
            }
          })
        }
      } else if (filter && filter.value !== null && filter.value !== '') {
        params[key] = filter.value
      }
    })
    return params
  }

  return (
    <MainContentLayout
      title={`Order Details #${order.state.supplierOrderNo}`}
      dt={dt}
      bottomToolbarLeft={bottomToolbarLeft}
    >
      <BackButton />

      <OrderDetailsHeader order={headerRow} />

      <Toolbar
        className='mb-2'
        left={topLeftToolbarTemplate}
        right={topRightToolbarTemplate}
      />
      <DataTable
        ref={dt}
        value={orderDetails}
        paginator
        paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        rowsPerPageOptions={rowsPerPageOptions}
        loading={tableLoading}
        dataKey='id'
        removableSort
        globalFilterFields={['manufacturer.name', 'molecule.name']}
        emptyMessage='No order details found.'
        filters={state.filters}
        sortField={state.sortField}
        sortOrder={state.sortOrder}
        rows={state.rows}
        first={state.first}
        onPage={(e) => setState((prev) => applyPageEvent(prev, e))}
        onSort={(e) => setState(e)}
        onFilter={(e) => {
          setState((prev) => ({ ...prev, filters: e.filters }))
          getData(e.filters)
        }}
      >
        <Column
          field='retailUpcCode'
          header='UPC'
          filterField='retailUpcCode'
          sortable
          filter
          showFilterMenuOptions={false}
          filterElement={upcCodeFilterElement}
        />
        <Column
          field='item.itemDescription'
          header='Product Description'
          filterField='item.itemDescription'
          filter
          filterElement={itemDescriptionFilterElement}
          sortable
          showFilterMenuOptions={false}
        />
        <Column
          header='Manufacturer Name'
          field='manufacturer.name'
          filter
          showFilterMenuOptions={false}
          sortable
          filterElement={manufacturerFilterElement}
        />
        <Column
          header='Molecule'
          field='molecule.name'
          showFilterMenuOptions={false}
          filter
          sortable
          hidden={
            order?.state?.supplier?.name === 'Origins Direct' ? true : false
          }
          filterElement={moleculeFilterElement}
        />
        <Column
          header='Requested Quantity'
          field='requestedQuantity'
          filter
          sortable
          hidden={order?.state?.supplier?.name === 'K+F' ? true : false}
          filterElement={requestedQuantityFilterElement}
          showFilterMenuOptions={false}
        />
        <Column
          header='Confirmed Quantity'
          field='shippedQuantity'
          filter
          sortable
          hidden={
            order?.state?.supplier?.name === 'K+F' ||
            !['Confirmed', 'Received'].includes(order?.state?.status)
              ? true
              : false
          }
          filterElement={shippedQuantityFilterElement}
          showFilterMenuOptions={false}
        />

        <Column
          header='Unit Price'
          field='unitPrice'
          body={unitPriceBodyTemplate}
          filterElement={unitPriceFilterElement}
          filter
          sortable
          showFilterMenuOptions={false}
        />
        <Column
          field='extendedPrice'
          header='Extended Price'
          body={extendedPriceBodyTemplate}
          filterElement={extendedPriceFilterElement}
          filter
          sortable
          showFilterMenuOptions={false}
        />
        {isAdmin && (
          <Column
            field='refundedAmount'
            header='PA Amount'
            body={refundedAmountBodyTemplate}
            filterElement={refundedAmountFilterElement}
            filter
            sortable
            showFilterMenuOptions={false}
          />
        )}
        {isAdmin && (
          <Column
            field='paPercentage'
            header='PA Percentage'
            body={paPercentageBodyTemplate}
            filterElement={paPercentageFilterElement}
            filter
            sortable
            showFilterMenuOptions={false}
          />
        )}
      </DataTable>

      {/* Admin cancel confirmation */}
      <Modals
        ModalHeader='Cancel Order'
        btnName='Confirm Cancellation'
        severity='danger'
        visible={cancelVisible}
        setVisible={setCancelVisible}
        onButtonClick={handleCancelOrder}
        loading={cancelLoading}
        disabled={cancelLoading}
        wait={true}
        width='36rem'
      >
        <div>
          <p className='mb-2'>
            Are you sure you want to cancel order{' '}
            <strong>#{headerRow?.supplierOrderNo}</strong>
            {headerRow?.status && headerRow.status !== 'Pending'
              ? ` (currently ${headerRow.status})`
              : ''}
            ? This cannot be undone.
          </p>
          {['Confirmed', 'Received'].includes(headerRow?.status) && (
            <Message
              severity='warn'
              className='w-100 mb-2'
              content={`This order was already ${headerRow.status}. If it was transmitted to the manufacturer, there is no automated EDI/AS2 reversal — the manufacturer will be notified by email.`}
            />
          )}
          <label className='form-label mt-2 d-block'>Reason (optional)</label>
          <InputTextarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={3}
            className='w-100'
            placeholder='Add a note explaining why this order is being cancelled'
          />
          {cancelError && (
            <Message
              severity='error'
              className='w-100 mt-2'
              content={cancelError}
            />
          )}
        </div>
      </Modals>

      {/* Admin reverse-cancellation confirmation */}
      <Modals
        ModalHeader='Reverse Cancellation'
        btnName='Confirm Reversal'
        severity='success'
        visible={reverseVisible}
        setVisible={setReverseVisible}
        onButtonClick={handleReverseOrder}
        loading={reverseLoading}
        disabled={reverseLoading}
        wait={true}
        width='36rem'
      >
        <div>
          <p className='mb-2'>
            Are you sure you want to reverse order{' '}
            <strong>#{headerRow?.supplierOrderNo}</strong> back to{' '}
            <strong>Pending</strong>?
          </p>
          <Message
            severity='info'
            className='w-100 mb-2'
            content='The line items will be restored to the quantities originally requested by the pharmacy, and the approval email will be resent to the reps for review.'
          />
          {reverseError && (
            <Message
              severity='error'
              className='w-100 mt-2'
              content={reverseError}
            />
          )}
        </div>
      </Modals>
    </MainContentLayout>
  )
}

export default OrderDetails
